import _ from "lodash";
import uuid from "time-uuid";
import { dataLock } from "@/data/DataUtils.ts";
import { OnlineOrder } from "@/data/OnlineOrder.ts";
import { OnlineOrderProductMappingCollection, type OnlineOrderProductMapping } from '@/data/OnlineOrderProductMapping.ts';
import { MarketPlaceProvider, type TOrder } from "@/pos/OrderType.ts";
import { computed, signal } from '@/react/core/reactive';
import { unwrapProxy } from "@/react/utils/common.ts";
import { userFLow } from "@/shared/logger.ts";
import { loginUser } from "@/data/UserSignal.ts";
import { PosScreen, router } from "@/pos/PosRouter.ts";
import { dialogNotiMatching } from "@/react/PendingOrder/dialogNotiMatching.tsx";
import { toast } from "react-toastify";
import { generalSetting0 } from "@/data/PosSettingsSignal.ts";
import { Product } from "@/data/Product.ts";
import { LL0 } from "@/react/core/I18nService.tsx";

export const [productMatches, setProductMatches] = signal<Array<OnlineOrderProductMapping>>([]);
export const productMatchesGroupByProvider = computed(() => _.groupBy(productMatches(), item => item.provider))
export const hasProductMatches = computed(() => productMatches().length > 0)
export const [editMenuMode, setEditMenuMode] = signal<'normal' | 'addCallback'>('normal')
export const [selectedOrder, setSelectedOrder] = signal<any>();
export const [selectedItemIdx, setSelectedItemIdx] = signal<number>(-1);
export const [provider, setProvider] = signal<string>('')
export const [selectedProduct, setSelectedProduct] = signal();
export const [productMatchingMode, setProductMatchingMode] = signal<'addNew' | 'edit'>('addNew');
export const [isProductsMatchingDialogOpen, setIsProductsMatchingDialogOpen] = signal(false);

// -> copy name & price for create product
export const [tempProductMatching, setTempProductMatching] = signal<Product | undefined>(undefined)
// _id product from pos (after create)
export const [productMatching, setProductMatching] = signal<string | undefined>(undefined)

export const handleClickAddProduct = (item: Product) => {
  setTempProductMatching(item)
  setEditMenuMode('addCallback')
  setIsProductsMatchingDialogOpen(false)
  router.screen = PosScreen.EDIT_MENU
  const title = generalSetting0()?.useMenuV2
    ? LL0().pendingOrder.pleaseCreateProduct()
    : LL0().pendingOrder.pleaseSelectEmptyItem()
  dialogNotiMatching(title).then()
}

export const addNewProduct = async () => {
  if (!productMatching()) {
    const title = generalSetting0()?.useMenuV2
      ? LL0().pendingOrder.pleaseCreateProduct()
      : LL0().pendingOrder.pleaseSelectEmptyItem()
    toast.warning(title)
    return
  }
  const posProductRaw = await Product.findOne({ selector: { _id: productMatching() } }).exec()
  const posProduct = posProductRaw?.toMutableJSON()
  if (productMatchingMode() === 'addNew') {
    await mapProductAndCorrectOrderItem(selectedOrder(), selectedItemIdx(), posProduct)
  } else {
    await mapProduct(provider(), selectedProduct(), productMatching())
  }
  router.screen = PosScreen.PENDING_ORDERS
  setTempProductMatching(undefined)
  setEditMenuMode('normal')
}

export const fetchProductMatches = async () => {
  try {
    const rxDocs: any = await OnlineOrderProductMappingCollection.find().exec() // <- here
    const docs = _.map(rxDocs, rxDoc => rxDoc.toMutableJSON())
    setProductMatches(docs)
  } catch (e) {
    console.error('Failed to fetch product matches', e)
  }
}

dataLock.acquireAsync().then(async () => {
  OnlineOrderProductMappingCollection.$.subscribe(fetchProductMatches)
  await fetchProductMatches()
})

/// -------
const requiredMappingFields = ['_id', 'id', 'sku', 'name']
function getExternalProductKey(provider: MarketPlaceProvider, item: any) {
  switch (provider) {
    case MarketPlaceProvider.RESTABLO:
      return `${item.name}__${item.sku || ''}`
    case MarketPlaceProvider.LIEFERANDO:
      return item.id;
    default:
      return item._id;
  }
}

export async function mapProductAndCorrectOrderItem(order: TOrder, itemIdx: number, posProduct?: Product) {
  const items = unwrapProxy(order.items)
  if (itemIdx >= items.length) {
    console.warn('[mapProduct] bad index')
    return
  }
  posProduct = unwrapProxy(posProduct)
  const original = _.cloneDeep(items[itemIdx])
  await mapProduct(order.provider!, original, posProduct?._id);

  // update items
  const externalProductKey = getExternalProductKey(order.provider!, original);
  const itemsWithTheSameKey = items.filter((item: any) => getExternalProductKey(order.provider!, item) === externalProductKey);

  for (const item of itemsWithTheSameKey) {
    // mapped
    item._id = posProduct?._id

    // printer
    item.labelPrinter = posProduct?.labelPrinter
    item.groupPrinter = posProduct?.groupPrinter
    item.groupPrinter2 = posProduct?.groupPrinter2

    // tax
    item.tax = posProduct?.tax
    item.tax2 = posProduct?.tax2
    item.taxCategory = posProduct?.taxCategory
    item.taxCategory2 = posProduct?.taxCategory2
    item.taxComponents = posProduct?.taxComponents
    item.taxComponents2 = posProduct?.taxComponents2
    item.taxes = [posProduct?.tax, posProduct?.tax2]
    // add more if needed
  }

  try {
    console.log('[mapProduct] update OnlineOrderCollection')
    const rawOrder = await OnlineOrder.findOne({ selector: { _id: order._id } }).exec()
    await rawOrder?.incrementalPatch({ items: items })
    console.log('[mapProduct] OnlineOrderCollection updated')
  } catch (e) {
    console.error('[mapProduct] failed to update items', e)
  }
}

export async function mapProduct(provider: MarketPlaceProvider, onlineOrderProduct: any, posProductId: string) {
  try {
    const externalProductKey = getExternalProductKey(provider, onlineOrderProduct);
    const existedDoc = await OnlineOrderProductMappingCollection.findOne({selector: {externalProductKey}}).exec()
    const change = {
      _id: existedDoc?._id || uuid(),
      externalProductKey,
      original: _.pick(onlineOrderProduct, requiredMappingFields),
      productId: posProductId,
      provider,
    }
    console.log('[mapProduct] saving OnlineOrderProductMappingCollection', externalProductKey, change)
    await OnlineOrderProductMappingCollection.upsert(change)
    console.log('[mapProduct] OnlineOrderProductMappingCollection saved')
    await fetchProductMatches()
  } catch (e) {
    console.error('[mapProduct] failed to save OnlineOrderProductMappingCollection', e)
  }
}

export async function unmapProduct(_id: string) {
  userFLow(`un map product ${_id}`, {
    username: loginUser()?.name
  });
  const pair = await OnlineOrderProductMappingCollection.findOne({selector: {_id}}).exec()
  await pair?.remove()
  await fetchProductMatches()
}